import { onMounted, onUnmounted } from 'vue';

import { useRaf } from '@resn/gozer-vue';

const MAX_DELTA_TIME = 100;

/*
    Spring helper based on Hooke's law (f = -k * x)
    and Newton's law of viscosity with damping: f = -k * x - b * v
    Returns force

    Example Use:
    // calculate delta time : dt
    velocity += force * dt / mass;
    position += velocity * dt;
*/
const spring = (current, target, velocity, tension, damping) => {
    const springForce = -tension * (current - target);
    const dampingForce = -damping * velocity;
    return springForce + dampingForce;
};

const initSpringState = (target, config = {}, targetValues = []) => {
    const state = {
        properties: {},
        config: {
            stiffness: 100,
            damping: 10,
            mass: 1,
            ...config,
        },
    };

    const newProperty = (key) => {
        state.properties[key] = {
            current: target[key],
            velocity: 0,
            to: target[key],
        };
    };

    const props = targetValues.length ? { ...targetValues } : target;

    if (targetValues.length)
        for (let i = 0; i < targetValues.length; i++) newProperty(targetValues[i]);
    else
        for (const key in props) {
            if (Object.hasOwnProperty.call(target, key)) newProperty(key);
        }

    return state;
};

const createSpringUpdate = (target, state) => {
    return ({ delta: dt }) => {
        const { stiffness, damping, mass } = state.config;

        for (const key in state.properties) {
            const prop = state.properties[key];
            const { current, to, velocity } = prop;
            const force = spring(current, to, velocity, stiffness, damping);

            const dtCapped = Math.min(dt, MAX_DELTA_TIME);
            const dt_ms = dtCapped / 1000;

            prop.velocity += (force * dt_ms) / mass;
            prop.current += prop.velocity * dt_ms;

            if (Math.abs(to - current) < 0.0001) prop.current = prop.to;
            target[key] = prop.current;
        }
    };
};

const createSpringControls = (target, state, startTick, stopTick, isActive) => {
    const update = createSpringUpdate(target, state);

    const set = (props, force = false) => {
        for (const key in props) {
            if (Object.hasOwnProperty.call(state.properties, key)) {
                state.properties[key].to = props[key];
                if (force) state.properties[key].current = props[key];
            }
        }
    };

    const start = () => startTick(update);
    const stop = () => stopTick();
    const toggle = () => (isActive() ? stop() : start());

    return { start, stop, toggle, set, update, state };
};

/** Imperative spring — safe to call outside setup() (watchers, callbacks). Caller must start/stop. */
export const createSpring = (target, config = {}, targetValues = []) => {
    const state = initSpringState(target, config, targetValues);
    let rafId = null;
    let lastTime = 0;
    let active = false;

    const startTick = (update) => {
        if (active) return;
        active = true;
        lastTime = 0;

        const tick = (time) => {
            const delta = lastTime ? time - lastTime : 0;
            lastTime = time;
            update({ delta });
            if (active) rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);
    };

    const stopTick = () => {
        active = false;
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    };

    return createSpringControls(target, state, startTick, stopTick, () => active);
};

export const useSpring = (target, config = {}, targetValues = []) => {
    const state = initSpringState(target, config, targetValues);
    const update = createSpringUpdate(target, state);
    const { stop: stopRaf, start: startRaf, isActive: activeTick } = useRaf(update, {
        immediate: false,
    });

    const controls = createSpringControls(
        target,
        state,
        () => startRaf(),
        () => stopRaf(),
        () => activeTick.value
    );

    const onVisibilityChange = () => {
        const isHidden = document.visibilityState === 'hidden';
        if (isHidden) controls.stop();
        else controls.start();
    };

    onMounted(() => {
        controls.start();
        document.addEventListener('visibilitychange', onVisibilityChange);
    });
    onUnmounted(() => {
        controls.stop();
        document.removeEventListener('visibilitychange', onVisibilityChange);
    });

    return controls;
};
